import { useState, useEffect, useCallback } from 'react';
import { fetchEvents } from '../api/client';
import type { TimelineEvent, FilterState } from '../types';

interface UseEventsResult {
  events: TimelineEvent[];
  total: number;
  loading: boolean;
  error: string | null;
  page: number;
  setPage: (p: number) => void;
  refresh: () => void;
}

const PAGE_SIZE = 50;

export function useEvents(filters: FilterState): UseEventsResult {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchEvents(filters, PAGE_SIZE, page * PAGE_SIZE);
      setEvents(result.data);
      setTotal(result.pagination.total);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [filters, page, refreshKey]);

  useEffect(() => { load(); }, [load]);

  const refresh = useCallback(() => {
    setPage(0);
    setRefreshKey((k) => k + 1);
  }, []);

  return { events, total, loading, error, page, setPage, refresh };
}
